import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../../context/AuthContext";
import api from "../../services/api";
import editIcon from "../../assets/edit-icon.png";
import deleteIcon from "../../assets/delete-icon.png";
import invoiceIcon from "../../assets/invoice-icon.png";

/* ================= REUSABLE SEARCH BAR ================= */
const SearchBar = ({ value, onChange, placeholder = "Search here..." }) => (
  <div style={styles.searchContainer}>
    <input
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={styles.searchInput}
    />
  </div>
);

/* ================= COLORS ================= */
const COLORS = {
  primary: "#0D47A1",
  danger: "#D32F2F",
  profit: "#16A34A",
  bg: "#FFF6EA",
  card: "#FFFFFF",
  border: "#E0E0E0",
  text: "#1E1E1E",
  muted: "#757575",
};

const Purchase = () => {
  const { role, user } = useAuth();

  const [purchases, setPurchases] = useState([]);
  const [parties, setParties] = useState([]);
  const [products, setProducts] = useState([]);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");

  // Search States
  const [searchTerm, setSearchTerm] = useState("");
  
  // Searchable Supplier States
  const [partySearch, setPartySearch] = useState("");
  const [showPartyDropdown, setShowPartyDropdown] = useState(false);

  // Searchable Product States
  const [productSearch, setProductSearch] = useState([]); 
  const [activeProductIndex, setActiveProductIndex] = useState(null);
  
  const [fromDate, setFromDate] = useState("");
const [toDate, setToDate] = useState("");

  // Quick Party States
  const [showQuickParty, setShowQuickParty] = useState(false);
  const [quickParty, setQuickParty] = useState({ name: "", phone: "", type: "supplier", balance: "" });

  const drawerRef = useRef(null);
  const partyDropdownRef = useRef(null);
  const productDropdownRef = useRef(null);

  const initialForm = {
    bill_no: "",
    party_id: "",
    purchase_date: new Date().toISOString().slice(0, 10),
    paid_amount: 0,
    discount_percent: 0,
    round_off: 0,
    items: [{ product_id: "", qty: "", rate: "" }],
  };

  const [form, setForm] = useState(initialForm);

  /* ================= LOAD DATA ================= */
  const loadData = async () => {
    try {
      const [purRes, partyRes, productRes] = await Promise.all([
        api.get("/purchase/list.php"),
        api.get("/parties/list.php"),
        api.get("/inventory/list.php"),
      ]);

      setPurchases(purRes.data?.data || []);
      setParties(partyRes.data?.data || []);
      setProducts(productRes.data?.data || []);
    } catch (err) {
      console.error("API Load Error:", err);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  /* ================= AUTO BILL NO GENERATION ================= */
  const generateBillNo = () => {
    const year = new Date().getFullYear();
    if (!purchases.length) return `PUR-${year}-001`;
    const last = purchases[0]?.bill_no || `PUR-${year}-000`;
    const parts = last.split("-");
    const lastNumber = parseInt(parts[parts.length - 1] || "0", 10);
    const nextNumber = String(lastNumber + 1).padStart(3, "0");
    return `PUR-${year}-${nextNumber}`;
  };

  useEffect(() => {
    if (showForm && !editingId) {
      setForm((prev) => ({ ...prev, bill_no: generateBillNo() }));
    }
  }, [showForm, purchases]);

  /* ================= OUTSIDE CLICK ================= */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showForm && drawerRef.current && !drawerRef.current.contains(e.target)) {
        if (!showQuickParty) resetForm();
      }
      if (showPartyDropdown && partyDropdownRef.current && !partyDropdownRef.current.contains(e.target)) {
        setShowPartyDropdown(false);
      }
      if (activeProductIndex !== null && productDropdownRef.current && !productDropdownRef.current.contains(e.target)) {
        setActiveProductIndex(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showForm, showQuickParty, showPartyDropdown, activeProductIndex]);

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setError("");
    setForm(initialForm);
    setShowQuickParty(false);
    setPartySearch("");
    setProductSearch([]);
    setActiveProductIndex(null);
    setQuickParty({ name: "", phone: "", type: "supplier", balance: "" });
  };

  /* ================= QUICK PARTY SAVE ================= */
  const handleQuickPartySave = async () => {
    if (!quickParty.name.trim()) return alert("Supplier name is required");
    try {
      await api.post("/parties/create.php", {
        ...quickParty,
        balance: parseFloat(quickParty.balance) || 0
      });
      
      const partyRes = await api.get("/parties/list.php");
      const updatedParties = partyRes.data?.data || [];
      setParties(updatedParties);
      
      const newParty = updatedParties.find(p => p.name === quickParty.name);
      if(newParty) {
        setForm({ ...form, party_id: newParty.id });
        setPartySearch(newParty.name);
      }

      setShowQuickParty(false);
      setQuickParty({ name: "", phone: "", type: "supplier", balance: "" });
    } catch (err) {
      alert("Failed to add supplier");
    }
  };

  /* ================= ITEM HANDLING ================= */
  const handleItemChange = (index, field, value) => {
    const updated = [...(form.items || [])];
    updated[index][field] = value;

    if (field === "product_id") {
      const selected = products.find((p) => String(p.id) === String(value));
      const newSearch = [...productSearch];
      newSearch[index] = selected ? selected.name : "";
      setProductSearch(newSearch);
      updated[index].rate = selected ? selected.price : "";
    }

    setForm({ ...form, items: updated });
  };

  const addItemRow = () => {
    setForm({
      ...form,
      items: [...(form.items || []), { product_id: "", qty: "", rate: "" }],
    });
    setProductSearch([...productSearch, ""]);
  };

  const removeItemRow = (index) => {
    const updated = [...(form.items || [])];
    updated.splice(index, 1);
    const updatedSearch = [...productSearch];
    updatedSearch.splice(index, 1);
    setForm({ ...form, items: updated });
    setProductSearch(updatedSearch);
  };

  /* ================= CALCULATIONS WITH AUTO ROUND OFF ================= */
  const subtotal = (form.items || []).reduce(
    (sum, item) => sum + Number(item.qty || 0) * Number(item.rate || 0),
    0
  );

  const discountAmount = (subtotal * parseFloat(form.discount_percent || 0)) / 100;
  const netAmount = subtotal - discountAmount;
  const grandTotal = Math.round(netAmount);
  const autoRoundOff = (grandTotal - netAmount).toFixed(2);

  const paid = form.paid_amount === "" ? 0 : Number(form.paid_amount);
  const selectedParty = parties.find((p) => String(p.id) === String(form.party_id));
  const partyBalance = Number(selectedParty?.balance || 0);
  const purchaseBalance = grandTotal - paid;
  const finalBalance = partyBalance + purchaseBalance;

const filteredPurchases = purchases.filter((p) => {
  const matchSearch =
    p.bill_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.party_name?.toLowerCase().includes(searchTerm.toLowerCase());

  const purchaseDate = new Date(p.purchase_date);

  const matchFrom = fromDate ? purchaseDate >= new Date(fromDate) : true;
  const matchTo = toDate ? purchaseDate <= new Date(toDate) : true;

  return matchSearch && matchFrom && matchTo;
});

  const filteredPartyDropdown = parties
    .filter(p => p.type?.toLowerCase() === "supplier")
    .filter(p => p.name.toLowerCase().includes(partySearch.toLowerCase()));

  /* ================= SUBMIT ================= */
  const submit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.bill_no || !form.party_id || form.items.length === 0) {
      setError("All required fields must be filled");
      return;
    }

    const payload = {
      ...form,
      total: grandTotal,
      round_off: autoRoundOff,
      discount_amount: discountAmount.toFixed(2),
      paid_amount: paid,
      user_id: user?.id
    };

    try {
      if (editingId) {
        await api.post("/purchase/update.php", { id: editingId, ...payload });
      } else {
        await api.post("/purchase/create.php", payload);
      }
      resetForm();
      loadData();
    } catch {
      setError("Error saving purchase");
    }
  };

  const handleEdit = (p) => {
    setForm({
      bill_no: p.bill_no || "",
      party_id: p.party_id || "",
      purchase_date: p.purchase_date || new Date().toISOString().slice(0, 10),
      paid_amount: p.paid_amount || 0,
      discount_percent: p.discount_percent || 0,
      round_off: p.round_off || 0,
      items: Array.isArray(p.items) && p.items.length > 0
          ? p.items
          : [{ product_id: "", qty: "", rate: "" }],
    });

    setPartySearch(p.party_name || "");
    const initialProductSearch = (p.items || []).map(item => {
      const prod = products.find(prod => String(prod.id) === String(item.product_id));
      return prod ? prod.name : "";
    });
    setProductSearch(initialProductSearch);

    setEditingId(p.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this purchase?")) return;
    await api.post("/purchase/delete.php", { id });
    loadData();
  };

  const handleInvoiceDownload = (id) => {
    window.open(`https://codesaarthi.online/khatasaarthi/api/purchase/invoice.php?id=${id}`, "_blank");
  };

const handleExport = () => {
  window.open(
    `https://codesaarthi.online/khatasaarthi/api/purchase/export.php?from=${fromDate}&to=${toDate}`,
    "_blank"
  );
};

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h2 style={styles.title}>Purchase Management</h2>
          <p style={styles.subtitle}>Manage purchase transactions</p>
          <SearchBar 
            value={searchTerm} 
            onChange={setSearchTerm} 
            placeholder="Search Bill No or Supplier..." 
          />
        </div>
 <div style={{ display: "flex", gap: 10, alignItems: "flex-start", flexWrap: "wrap" }}>

  {/* DATE FILTER */}
  <div style={{ display: "flex", gap: 8 }}>
    <input
      type="date"
      value={fromDate}
      onChange={(e) => setFromDate(e.target.value)}
      style={styles.searchInput}
    />
    <input
      type="date"
      value={toDate}
      onChange={(e) => setToDate(e.target.value)}
      style={styles.searchInput}
    />
  </div>

  <button style={styles.newBtn} onClick={() => setShowForm(true)}>
    + Add Purchase 
  </button>

  <button
    style={{ ...styles.newBtn, background: "#16A34A" }}
    onClick={() =>
      window.open(
        `https://codesaarthi.online/khatasaarthi/api/purchase/export.php?from=${fromDate}&to=${toDate}`,
        "_blank"
      )
    }
  >
    Export Excel
  </button>
</div>
      </div>

      <div style={styles.card}>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>#</th>
                <th style={styles.th}>Bill No</th>
                <th style={styles.th}>Supplier</th>
                <th style={styles.th}>Date</th>
                <th style={styles.th}>Disc%</th>
                <th style={styles.th}>Total</th>
                <th style={styles.th}>Paid</th>
                <th style={styles.th}>Balance</th>
                <th style={styles.thCenter}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPurchases.map((p, index) => (
                <tr key={p.id}>
                  <td style={styles.td}>{index + 1}</td>
                  <td style={styles.tdBold}>{p.bill_no}</td>
                  <td style={styles.td}>{p.party_name}</td>
                  <td style={styles.td}>{p.purchase_date}</td>
                  <td style={styles.td}>{p.discount_percent || 0}%</td>
                  <td style={styles.tdBold}>₹ {p.total}</td>
                  <td style={styles.td}>₹ {p.paid_amount}</td>
                  <td style={styles.td}>
                    {p.balance_amount > 0 ? (
                      <span style={styles.lowBadge}>₹ {p.balance_amount}</span>
                    ) : (
                      <span style={styles.okBadge}>₹ {p.balance_amount}</span>
                    )}
                  </td>
                  <td style={styles.actionCell}>
                    <div style={styles.iconWrapper}>
                      <button style={styles.iconBtn} onClick={() => handleInvoiceDownload(p.id)}>
                        <img src={invoiceIcon} alt="invoice" style={styles.icon} />
                      </button>
                      {role !== "SALESMAN" && (
                        <>
                          <button style={styles.iconBtn} onClick={() => handleEdit(p)}>
                            <img src={editIcon} alt="edit" style={styles.icon} />
                          </button>
                          <button style={styles.iconBtn} onClick={() => handleDelete(p.id)}>
                            <img src={deleteIcon} alt="delete" style={styles.icon} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && createPortal(
        <div style={styles.overlay}>
          <div ref={drawerRef} style={styles.drawer}>
            <div style={styles.drawerHeader}>
              <h2 style={styles.drawerTitle}>{editingId ? "Edit Purchase" : "Add Purchase"}</h2>
              <button style={styles.closeBtn} onClick={resetForm}>✕</button>
            </div>

            <form onSubmit={submit}>
              <div style={styles.grid}>
                <div style={styles.field}>
                  <label style={styles.label}>Bill No</label>
                  <input
                    value={form.bill_no}
                    onChange={(e) => setForm({ ...form, bill_no: e.target.value })}
                    style={styles.input}
                  />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Purchase Date</label>
                  <input
                    type="date"
                    value={form.purchase_date}
                    onChange={(e) => setForm({ ...form, purchase_date: e.target.value })}
                    style={styles.input}
                  />
                </div>
                
                <div style={{ ...styles.field, marginBottom: 20, position: 'relative' }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <label style={styles.label}>Supplier</label>
                    <button 
                      type="button" 
                      onClick={() => setShowQuickParty(!showQuickParty)}
                      style={styles.quickAddToggle}
                    >
                      {showQuickParty ? "✕ Cancel" : "+ Add New Supplier"}
                    </button>
                  </div>

                  {!showQuickParty ? (
                    <div ref={partyDropdownRef} style={{ position: 'relative' }}>
                      <input
                        placeholder="Search supplier..."
                        value={partySearch}
                        onChange={(e) => {
                          setPartySearch(e.target.value);
                          setShowPartyDropdown(true);
                        }}
                        onFocus={() => setShowPartyDropdown(true)}
                        style={styles.input}
                      />
                      {showPartyDropdown && partySearch && (
                        <div style={styles.dropdownMenu}>
                          {filteredPartyDropdown.length > 0 ? (
                            filteredPartyDropdown.map((p) => (
                              <div 
                                key={p.id} 
                                style={styles.dropdownItem}
                                onClick={() => {
                                  setForm({ ...form, party_id: p.id });
                                  setPartySearch(p.name);
                                  setShowPartyDropdown(false);
                                }}
                              >
                                {p.name}
                              </div>
                            ))
                          ) : (
                            <div style={styles.noResult}>No supplier found</div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div style={styles.quickPartyBox}>
                      <input
                        placeholder="Supplier Name"
                        value={quickParty.name}
                        onChange={(e) => setQuickParty({ ...quickParty, name: e.target.value })}
                        style={{ ...styles.input, marginBottom: 8 }}
                      />
                      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                        <input
                          placeholder="Phone"
                          value={quickParty.phone}
                          maxLength={10}
                          onChange={(e) => setQuickParty({ ...quickParty, phone: e.target.value })}
                          style={styles.input}
                        />
                        <select
                          value={quickParty.type}
                          onChange={(e) => setQuickParty({ ...quickParty, type: e.target.value })}
                          style={styles.input}
                        >
                          <option value="supplier">Supplier</option>
                          <option value="customer">Customer</option>
                        </select>
                      </div>
                      <input
                        placeholder="Opening Balance"
                        type="number"
                        value={quickParty.balance}
                        onChange={(e) => setQuickParty({ ...quickParty, balance: e.target.value })}
                        style={{ ...styles.input, marginBottom: 8 }}
                      />
                      <button 
                        type="button" 
                        onClick={handleQuickPartySave}
                        style={{ ...styles.primaryBtn, width: "100%", height: 38, fontSize: 13 }}
                      >
                        Save & Select Supplier
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {(form.items || []).map((item, index) => (
                <div key={index} style={styles.itemRow}>
                  <div style={{ position: 'relative' }} ref={activeProductIndex === index ? productDropdownRef : null}>
                    <input
                      placeholder="Search product..."
                      value={productSearch[index] || ""}
                      onChange={(e) => {
                        const newSearch = [...productSearch];
                        newSearch[index] = e.target.value;
                        setProductSearch(newSearch);
                        setActiveProductIndex(index);
                      }}
                      onFocus={() => setActiveProductIndex(index)}
                      style={styles.input}
                    />
                    {activeProductIndex === index && productSearch[index] && (
                      <div style={styles.dropdownMenu}>
                        {products
                          .filter(p => p.name.toLowerCase().includes((productSearch[index] || "").toLowerCase()))
                          .map(p => (
                            <div 
                              key={p.id} 
                              style={styles.dropdownItem}
                              onClick={() => {
                                handleItemChange(index, "product_id", p.id);
                                setActiveProductIndex(null);
                              }}
                            >
                              {p.name}
                            </div>
                          ))
                        }
                      </div>
                    )}
                  </div>
                  <input
                    type="number"
                    placeholder="Qty"
                    value={item.qty}
                    onChange={(e) => handleItemChange(index, "qty", e.target.value)}
                    style={styles.input}
                  />
                  <input
                    type="number"
                    placeholder="Rate"
                    value={item.rate}
                    onChange={(e) => handleItemChange(index, "rate", e.target.value)}
                    style={styles.input}
                  />
                  <button
                    type="button"
                    onClick={() => removeItemRow(index)}
                    style={styles.smallBtn}
                    disabled={form.items.length === 1}
                  >Remove</button>
                </div>
              ))}

              <button type="button" onClick={addItemRow} style={styles.smallBtn}>+ Add Item</button>

              <div style={styles.summaryWrapper}>
                <div style={styles.summaryRow}>
                  <span>Subtotal</span>
                  <strong>₹ {subtotal.toFixed(2)}</strong>
                </div>
                <div style={styles.summaryRow}>
                  <span>Discount (%)</span>
                  <input
                    type="number"
                    value={form.discount_percent}
                    onChange={(e) => setForm({ ...form, discount_percent: e.target.value })}
                    style={styles.summaryInput}
                  />
                </div>
                <div style={styles.summaryRow}>
                  <span>Round Off</span>
                  <span style={{color: COLORS.muted, fontWeight: '500'}}>{autoRoundOff}</span>
                </div>
                <div style={styles.summaryRow}>
                  <span><strong>Grand Total</strong></span>
                  <strong style={{fontSize: 22, color: COLORS.primary}}>₹ {grandTotal}</strong>
                </div>
                <div style={styles.summaryRow}>
                  <span>Paid Amount</span>
                 <input
  type="number"
  value={form.paid_amount}
  onChange={(e) =>
    setForm({
      ...form,
      paid_amount: e.target.value === "" ? "" : Number(e.target.value)
    })
  }
  style={{...styles.summaryInput, borderColor: COLORS.primary, fontWeight: 'bold'}}
/>
                </div>
                <hr style={{margin: '10px 0', opacity: 0.2}} />
                <div style={styles.summaryRow}>
                  <span>Net Supplier Balance</span>
                  <strong style={{ color: finalBalance > 0 ? COLORS.danger : COLORS.profit }}>
                    ₹ {finalBalance.toFixed(2)}
                  </strong>
                </div>
              </div>

              {error && <p style={styles.error}>{error}</p>}

              <div style={styles.actionRow}>
                <button type="button" style={styles.cancelBtn} onClick={resetForm}>Cancel</button>
                <button type="submit" style={styles.primaryBtn}>{editingId ? "Update Purchase" : "Save Purchase"}</button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

/* ================= STYLES ================= */
const styles = {
  searchContainer: { marginTop: "12px", width: "300px" },
  searchInput: { width: "100%", padding: "8px 12px", borderRadius: "8px", border: `1px solid ${COLORS.border}`, fontSize: "14px", outline: "none" },
  dropdownMenu: { position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: `1px solid ${COLORS.border}`, borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10, maxHeight: '200px', overflowY: 'auto', marginTop: '4px' },
  dropdownItem: { padding: '10px 12px', cursor: 'pointer', fontSize: '14px', borderBottom: `1px solid #f0f0f0` },
  noResult: { padding: '10px 12px', color: COLORS.muted, fontSize: '13px' },
  page: { background: COLORS.bg, padding: "10px clamp(15px,4vw,30px) 30px", minHeight: "100vh" },
  header: { display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 30 },
  headerLeft: { display: "flex", flexDirection: "column" },
  title: { fontSize: 28, fontWeight: 700, marginBottom: 6 },
  subtitle: { color: COLORS.muted },
  newBtn: { background: COLORS.primary, color: "#fff", padding: "10px 20px", borderRadius: 10, border: "none", fontWeight: 600, height: 42, cursor: "pointer" },
  card: { background: COLORS.card, borderRadius: 18, padding: "25px 30px" },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", minWidth: 850 },
  th: { padding: "14px 12px", textAlign: "left", fontWeight: 600, color: COLORS.muted, borderBottom: `1px solid ${COLORS.border}` },
  thCenter: { padding: "14px 12px", textAlign: "center", fontWeight: 600, color: COLORS.muted, borderBottom: `1px solid ${COLORS.border}` },
  td: { padding: "16px 12px", borderBottom: `1px solid ${COLORS.border}` },
  tdBold: { padding: "16px 12px", fontWeight: 600, borderBottom: `1px solid ${COLORS.border}` },
  actionCell: { borderBottom: `1px solid ${COLORS.border}`, textAlign: "center" },
  iconWrapper: { display: "flex", justifyContent: "center", gap: 16 },
  iconBtn: { background: "transparent", border: "none", cursor: "pointer" },
  icon: { width: 18, height: 18 },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", justifyContent: "flex-end", zIndex: 9999 },
  drawer: { width: "min(600px, 100%)", height: "100vh", background: "#fff", padding: "20px 30px 100px", overflowY: "auto", boxShadow: "-6px 0 20px rgba(0,0,0,0.15)", position: "relative", boxSizing: "border-box" },
  drawerHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 },
  drawerTitle: { fontSize: 22, fontWeight: 700 },
  closeBtn: { border: "none", background: "none", fontSize: 20, cursor: "pointer" },
  grid: { display: "flex", flexDirection: "column", gap: 18 },
  field: { display: "flex", flexDirection: "column" },
  label: { marginBottom: 6, color: COLORS.muted },
  input: { width: "100%", padding: "10px 12px", borderRadius: 6, border: `1px solid ${COLORS.border}`, boxSizing: "border-box" },
  itemRow: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))", gap: 16, alignItems: "center", marginBottom: 12 },
  smallBtn: { padding: "6px 14px", borderRadius: 6, border: `1px solid ${COLORS.border}`, background: "#fff", cursor: "pointer" },
  summaryWrapper: { width: "min(320px,100%)", marginLeft: "auto", marginTop: 30, marginBottom: 30 },
  summaryRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 },
  summaryInput: { width: 100, padding: "8px 10px", borderRadius: 6, border: `1px solid ${COLORS.border}`, textAlign: "right" },
  actionRow: { display: "flex", justifyContent: "flex-end", flexWrap: "wrap", marginTop: 30, gap: 12 },
  cancelBtn: { padding: "8px 18px", borderRadius: 6, border: `1px solid ${COLORS.border}`, background: "#fff" },
  primaryBtn: { padding: "8px 20px", borderRadius: 6, border: "none", background: COLORS.primary, color: "#fff", fontWeight: 600 },
  lowBadge: { background: "#FFEBEE", color: COLORS.danger, padding: "5px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600 },
  okBadge: { background: "#E3F2FD", color: COLORS.primary, padding: "5px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600 },
  error: { marginTop: 12, color: COLORS.danger },
  quickAddToggle: { border: 'none', background: 'none', color: COLORS.primary, cursor: 'pointer', fontSize: 12, fontWeight: 700 },
  quickPartyBox: { background: "#F8F9FA", padding: 15, borderRadius: 10, border: `1px dashed ${COLORS.border}`, marginTop: 5 }
};

export default Purchase;